import { useEffect, useRef, useState } from 'react'
import { languageInfo } from '../i18n/languages'
import {
  BuiltInSpeaker,
  builtInVoiceSupported,
  findVoice,
  loadVoices,
  Speaker,
  speechSupported,
  prepareBuiltIn,
  toBuiltInChunks,
  toSpeechChunks,
  type SpeechState
} from '../services/speech'
import type { Language } from '../types/document'

/**
 * "Sunao": speaks with the phone's own voice when it has one for the chosen
 * language, otherwise with Samjho's built-in neural voice (Meta MMS, running
 * on the device), so every Indian language can be heard on every device.
 */
export function useSpeech() {
  const [state, setState] = useState<SpeechState>('idle')
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [voicesLoaded, setVoicesLoaded] = useState(false)
  const nativeRef = useRef<Speaker | null>(null)
  const builtInRef = useRef<BuiltInSpeaker | null>(null)
  const activeRef = useRef<Speaker | BuiltInSpeaker | null>(null)
  if (!nativeRef.current) nativeRef.current = new Speaker(setState)
  if (!builtInRef.current) builtInRef.current = new BuiltInSpeaker(setState)

  useEffect(() => {
    let alive = true
    loadVoices().then((list) => {
      if (!alive) return
      setVoices(list)
      setVoicesLoaded(true)
    })
    const native = nativeRef.current
    const builtIn = builtInRef.current
    return () => {
      alive = false
      native?.stop()
      builtIn?.stop()
    }
  }, [])

  const nativeVoiceFor = (language: Language) =>
    speechSupported() ? findVoice(voices, languageInfo(language).bcp47) : null

  const hasBuiltIn = (language: Language) => builtInVoiceSupported() && languageInfo(language).voice !== null

  function stopAll() {
    nativeRef.current?.stop()
    builtInRef.current?.stop()
    activeRef.current = null
  }

  return {
    supported: speechSupported() || builtInVoiceSupported(),
    state,
    voicesLoaded,
    /** True when this language can be spoken on this device, one way or another. */
    hasVoice: (language: Language) =>
      nativeVoiceFor(language) !== null || hasBuiltIn(language) || (speechSupported() && !voicesLoaded),
    /** True when Samjho will use its own voice instead of the phone's. */
    usesBuiltInVoice: (language: Language) => voicesLoaded && nativeVoiceFor(language) === null && hasBuiltIn(language),
    play(sentences: string[], language: Language) {
      stopAll()
      const chunks = toSpeechChunks(sentences)
      const voice = nativeVoiceFor(language)
      if (voice) {
        activeRef.current = nativeRef.current
        nativeRef.current?.play(chunks, languageInfo(language).bcp47, voice)
      } else if (hasBuiltIn(language)) {
        activeRef.current = builtInRef.current
        builtInRef.current?.play(toBuiltInChunks(sentences), language)
      } else if (speechSupported()) {
        // Last resort: let the browser try the language tag without a matched voice
        activeRef.current = nativeRef.current
        nativeRef.current?.play(chunks, languageInfo(language).bcp47, null)
      }
    },
    /** Background warm-up for the built-in voice (only if it is already downloaded). */
    prepare(sentences: string[], language: Language) {
      if (voicesLoaded && nativeVoiceFor(language) === null && hasBuiltIn(language)) void prepareBuiltIn(sentences, language)
    },
    pause: () => activeRef.current?.pause(),
    resume: () => activeRef.current?.resume(),
    stop: stopAll
  }
}
